import { useEffect, useRef } from 'react';
import { Bell, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNotificationStack, StackNotification } from '@/hooks/useNotificationStack';
import { NotificationCard } from './NotificationCard';
import { useNavigate } from 'react-router-dom';

export const NotificationStack = () => {
  const {
    notifications,
    unreadCount,
    removeNotification,
    markAsRead,
    clearStack,
    isMinimized,
    toggleMinimized,
  } = useNotificationStack();

  const navigate = useNavigate();
  const prevNotificationCount = useRef(0);

  // Auto-expand when a new notification arrives
  useEffect(() => {
    if (notifications.length > prevNotificationCount.current && isMinimized) {
      // New notification arrived, expand to show it
      toggleMinimized();
    }
    prevNotificationCount.current = notifications.length;
  }, [notifications.length, isMinimized, toggleMinimized]);

  // Only show when there are notifications
  // Hide completely when no notifications exist
  if (notifications.length === 0 && unreadCount === 0) {
    return null;
  }

  const handleView = (notification: StackNotification) => {
    markAsRead(notification.id);
    
    if (notification.type === 'support_message' && notification.data?.thread_id) {
      navigate(`/support/${notification.data.thread_id}`);
    } else if (notification.type === 'business_metric') {
      navigate('/business-metrics');
    }
  };

  return (
    <div
      className={`
        fixed top-4 right-4 z-50
        transition-all duration-300 ease-in-out
        ${isMinimized ? 'transform translate-y-0' : ''}
      `}
      style={{
        maxWidth: '400px',
        width: '100%',
      }}
    >
      {isMinimized ? (
        // Minimized state - just show badge (only visible when there are notifications)
        <div className="flex items-center justify-end">
          <Button
            variant="outline"
            size="sm"
            className="relative rounded-full h-10 w-10 p-0 bg-white dark:bg-gray-800 shadow-lg border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400"
            onClick={toggleMinimized}
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </Badge>
            )}
          </Button>
        </div>
      ) : (
        // Expanded state - show notification stack
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-gray-700 dark:text-gray-300" />
              <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                Notifications
              </h3>
              {unreadCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {unreadCount} new
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {notifications.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={clearStack}
                >
                  Clear all
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={toggleMinimized}
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Notifications List */}
          {notifications.length > 0 ? (
            <ScrollArea className="h-[500px] p-4">
              <div className="space-y-2">
                {notifications.map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    notification={notification}
                    onDismiss={removeNotification}
                    onMarkAsRead={markAsRead}
                    onView={handleView}
                  />
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <Bell className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No notifications</p>
            </div>
          )}

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs"
                onClick={() => navigate('/notifications')}
              >
                View all notifications
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

